import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, shareReplay, switchMap, catchError, map } from 'rxjs';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { AppVersion } from './services/version/version.service';

/**
 * Fetches a language catalogue, keyed by the build that produced it.
 *
 * A catalogue is immutable for a given release, so the URL carries the build
 * identity and a new deploy asks for a URL the browser has never seen. That is
 * what makes "cache it and reuse it until the version changes" safe: there is
 * no expiry to tune and no stale window to reason about.
 *
 * The build identity comes from assets/version.json, which tools/versioning
 * rewrites on every build - the same file that already records the sha and
 * branch. It is read once per session and shared, so the extra request happens
 * on first translation only.
 *
 * If version.json cannot be read the catalogue is still fetched, just without a
 * cache key. Translations working is more important than cache precision.
 */
@Injectable({ providedIn: 'root' })
export class ErsTranslocoLoader implements TranslocoLoader {
  private readonly _http = inject(HttpClient);

  /** Resolved once, then replayed - not re-fetched per language. */
  private readonly _buildKey$: Observable<string> = this._http
    .get<AppVersion>('assets/version.json')
    .pipe(
      map((v) => [v?.version, v?.sha?.slice(0, 7)].filter(Boolean).join('-')),
      catchError(() => of('')),
      shareReplay({ bufferSize: 1, refCount: false })
    );

  getTranslation(lang: string): Observable<Translation> {
    return this._buildKey$.pipe(
      switchMap((key) =>
        this._http.get<Translation>(
          key
            ? `assets/i18n/${lang}.json?v=${encodeURIComponent(key)}`
            : `assets/i18n/${lang}.json`
        )
      )
    );
  }
}
